import React from 'react';
import {useIncidentDeleteMutation, useIncidentRegistryQuery, useIncidentSettingsQuery} from "../query";
import {useFeedbackTools} from "../../../components/FeedbackTools";
import {useTranslation} from "react-i18next";
import {useIncidentSchema} from "../../../schemaValidations/incidentSchema";
import {useFormik} from "formik";
import MDBox from "../../../materialDashboard/components/MDBox";
import MDTypography from "../../../materialDashboard/components/MDTypography";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Localization from "./Localization";
import Conditions from "./Conditions";
import Incident from "./Incident";
import Attachments from "./Attachments";
import PeopleCaught from "./PeopleCaught";
import MDButton from "../../../materialDashboard/components/MDButton";
import MDLoadingButton from "../../../components/MDLoadingButton";
import {sequentialKeyGenerator} from "../../../utils/sequentialKeyGenerator";
import useResourceAuthorization from "../../auth/useResourceAuthorization";
import AuditInfo from "../../../components/AuditInfo";
import LoadingOverlay from "../../../components/LoadingOverlay";


const scrollMarginTop = "116px";
export const personCaughtKeyGenerator = sequentialKeyGenerator();
export const attachmentKeyGenerator = sequentialKeyGenerator();

export const formikPropertyChanged = (prevProps, nextProps, properties) => {
    return properties.every(property =>
        (prevProps.formik.values[property] === nextProps.formik.values[property]
            && prevProps.formik.errors[property] === nextProps.formik.errors[property]
            && prevProps.formik.touched[property] === nextProps.formik.touched[property]
        ));
}


const IncidentForm = ({title, onSubmit, incident}) => {
    const incidentRegistry = useIncidentRegistryQuery();
    const incidentSettings = useIncidentSettingsQuery();
    const incidentDeletionMutation = useIncidentDeleteMutation();
    const {askConfirmation} = useFeedbackTools();
    const {t} = useTranslation(['incidents', 'validation']);

    const authorization = useResourceAuthorization(incident);
    const validationSchema = useIncidentSchema();

    const inputDisabled = !authorization.edit;

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            // Localization
            date: incident?.date,
            timeRange: incident?.timeRange,
            datetime: incident?.datetime,
            workgroupId: incident?.workgroupId,
            regionId: incident?.regionId,
            provinceId: incident?.provinceId,
            municipalityId: incident?.municipalityId,
            locationName: incident?.locationName,
            elevationRelease: incident?.elevationRelease,
            aspect: incident?.aspect,
            geom: {
                type: incident?.geom?.type ?? 'Point',
                coordinates: incident?.geom?.coordinates ?? [null, null]
            },

            // Conditions
            snowHeightMin: incident?.snowHeightMin,
            snowHeightMax: incident?.snowHeightMax,
            slopeAngleReleaseRange: incident?.slopeAngleReleaseRange,
            slopeAngleRelease: incident?.slopeAngleRelease,
            avalancheType: incident?.avalancheType,
            avalancheSize: incident?.avalancheSize,
            ww: incident?.ww,
            daysSinceLastSnowfall: incident?.daysSinceLastSnowfall,
            nearRidge: incident?.nearRidge,
            dangerScaleBulletin: incident?.dangerScaleBulletin,
            tap1Bulletin: incident?.tap1Bulletin,
            tap2Bulletin: incident?.tap2Bulletin,
            tap1Incident: incident?.tap1Incident,
            tap2Incident: incident?.tap2Incident,

            // Incident
            alpineRescue: incident?.alpineRescue,
            incidentCategory: incident?.incidentCategory,
            incidentSubcategory: incident?.incidentSubcategory,
            groupWithProfessionalGuide: incident?.groupWithProfessionalGuide ?? false,
            numberOfPeopleInvolved: incident?.numberOfPeopleInvolved,
            notes: incident?.notes,

            // People Caught
            peopleCaught: incident?.peopleCaught?.map(person => ({...person, key: `id-${person.id}`})),

            // Attachments
            attachments: incident?.attachments?.map(attachment => ({...attachment, key: `id-${attachment.id}`})),
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => await onSubmit(values)
    });

    const handleDelete = async () => {
        const innerDeleteIncident = async () => {
            await incidentDeletionMutation.mutateAsync(incident.id);
        }
        await askConfirmation({
            title: t('incidentForm.deleteConfirmationTitle'),
            description: t('incidentForm.deleteDescription'),
            confirmationText: t('incidentForm.deleteConfirmationText'),
            onConfirm: innerDeleteIncident
        });
    }

    const submitDisabled = false;  // TODO add a generic method to check if a form submit should be enabled

    if (incidentRegistry.isLoading || incidentSettings.isLoading) {
        return <LoadingOverlay />
    }

    const hasError = (formik.submitCount > 0 && !formik.isValid);

    return <>
        <Card sx={{mt: 4, px: 3, pb: 3}}>
            <MDBox variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info"
                   mx={2} mt={-3} p={2} mb={1} textAlign="center">
                <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
                    {title}
                </MDTypography>
            </MDBox>
            <Grid container rowSpacing={3} sx={{"& > *": {scrollMarginTop: scrollMarginTop}}}>
                <Grid item xs={12}>
                    <Localization formik={formik} incidentRegistry={incidentRegistry} inputDisabled={inputDisabled} incident={incident}/>
                </Grid>
                <Grid item xs={12}>
                    <Conditions formik={formik} incidentRegistry={incidentRegistry} inputDisabled={inputDisabled}/>
                </Grid>
                <Grid item xs={12}>
                    <Incident formik={formik} incidentRegistry={incidentRegistry} incidentSettings={incidentSettings}
                              inputDisabled={inputDisabled}
                              numberOfPeopleCaught={formik.values.peopleCaught?.length ?? 0}/>
                </Grid>
                <Grid item xs={12}>
                    <PeopleCaught formik={formik} incidentRegistry={incidentRegistry} inputDisabled={inputDisabled}/>
                </Grid>
                <Grid item xs={12}>
                    <Attachments formik={formik} incidentRegistry={incidentRegistry} inputDisabled={inputDisabled}/>
                </Grid>
                {
                    hasError && <Grid item xs={12} sx={{display: "flex", justifyContent: "center"}} >
                        <MDTypography variant="button" color="error" fontWeight="regular">
                            {t('incidentForm.fixErrorsToProceed')}
                        </MDTypography>
                    </Grid>
                }

                <Grid item xs={12} sx={{display: "flex", justifyContent: "space-between", gap: 2}}>
                    <div>
                        {
                            authorization.delete && <MDButton variant="outlined" color="error" onClick={handleDelete}>
                                {t('incidentForm.delete')}
                            </MDButton>
                        }
                    </div>
                    {
                        authorization.edit && <MDLoadingButton onClick={formik.handleSubmit}
                                                                 name="saveButton"
                                                                 label="save"
                                                                 loading={formik.isSubmitting}
                                                                 disabled={submitDisabled}
                                                                 variant="gradient"
                                                                 color="info">
                            {t('incidentForm.save')}
                        </MDLoadingButton>
                    }
                </Grid>
            </Grid>
        </Card>
        <AuditInfo {...incident} />
    </>
}

export default IncidentForm;